// /**
//  * Products - list
//  */

// React
import React, {useContext} from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {mdiChevronDown, mdiChevronUp, mdiFormatListCheckbox} from '@mdi/js';

// Type
import {VariantInventory} from '../interfaces';
import {useHistory} from 'react-router-dom';
import AuthContext from '../../../authContext';
import Can from '../../../components/Can';
import jMoment from 'moment-jalaali';

type Data = VariantInventory;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpen: (desc: string, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      padding: '10px 8px',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    body: {
      textAlign: 'left',
      padding: '10px 16px',
      borderRadius: '0',
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      // '& .MuiTableCell-root': {
      //   padding: '24px',
      // },
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    columnText: {
      padding: '0 10px',
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const {order, orderBy, onRequestSort} = props;
  const classes = useStyles();
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  let headCells: HeadCell[] = [];

  Can({
    role: authContext.user.role,
    perform: 'products:inventory',
    yes: () => {
      headCells = [
        {
          id: 'serial',
          label: 'سریال',
        },
        {
          id: 'productionDate',
          label: 'تاریخ تولید',
        },
        {
          id: 'expirationDate',
          label: 'تاریخ انقضا',
        },
        {
          id: 'deliveryDate',
          label: 'تاریخ ورود',
        },
        {
          id: 'parcelId',
          label: 'شناسه محموله ورودی',
        },
        {
          id: 'amount',
          label: 'موجودی',
        },
        {
          id: 'unitCost',
          label: 'قیمت (تومان)',
        },
      ];
    },
    no: () => {
      headCells = [
        {
          id: 'serial',
          label: 'سریال',
        },
        {
          id: 'productionDate',
          label: 'تاریخ تولید',
        },
        {
          id: 'expirationDate',
          label: 'تاریخ انقضا',
        },
        {
          id: 'deliveryDate',
          label: 'تاریخ ورود',
        },
        {
          id: 'parcelId',
          label: 'شناسه محموله ورودی',
        },
        {
          id: 'unitCost',
          label: 'قیمت (تومان)',
        },
      ];
    },
  });

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.serialNumber}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.productionDate &&
                        jMoment(row.productionDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.expirationDate &&
                        jMoment(row.expirationDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.deliveryDate &&
                        jMoment(row.deliveryDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.parcelId}
                    </StyledTableCell>
                    <Can
                      role={authContext.user.role}
                      perform="products:inventory"
                      yes={() => (
                        <StyledTableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          align="center"
                        >
                          {row.amount}
                        </StyledTableCell>
                      )}
                    />
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {new Intl.NumberFormat('fa-IR').format(row.unitCost)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {/* <IconButton
                        title="توضیحات"
                        size="small"
                        onClick={() =>
                          handleOpen(row.description, 'description')
                        }
                      >
                        <Icon path={mdiFormatListCheckbox} size={1} />
                      </IconButton> */}
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
